import _ from 'lodash';
import moment from 'moment';

import * as api from '../../../../api';
import { Named } from '../../../../components/Guarantor/utils';
import { EMPLOYMENT_STATUS_SHOW_EMPLOYER } from 'common/dist/utils/previsitFormField';

export const SELF_ONLY_PAYORS = new Set<api.PatientInsuranceInformation.Payor>([
  // TODO: UPDATE THIS LIST. PAYORS ENUM BEING DEPRECATED IN FAVOR OF PAYORS TABLE
  api.PatientInsuranceInformation.Payor.AarpMedicareAdvantageUnitedHealthcare,
  api.PatientInsuranceInformation.Payor.AetnaMedicare,
  api.PatientInsuranceInformation.Payor.BlueCrossMedicareAdvantage,
  api.PatientInsuranceInformation.Payor.DellChildrenChip,
  api.PatientInsuranceInformation.Payor.DellChildrenMedicaid,
  api.PatientInsuranceInformation.Payor.HumanaMedicareAdvantage,
  api.PatientInsuranceInformation.Payor.HumanaMedicareAdvantageSupplement,
  api.PatientInsuranceInformation.Payor.Medicaid,
  api.PatientInsuranceInformation.Payor.MedicaidAmerigroup,
  api.PatientInsuranceInformation.Payor.MedicaidBcbsStar,
  api.PatientInsuranceInformation.Payor.MedicaidKansas,
  api.PatientInsuranceInformation.Payor.MedicaidMissouri,
  api.PatientInsuranceInformation.Payor.MedicaidSuperiorHealthPlan,
  api.PatientInsuranceInformation.Payor.MedicaidTmhp,
  api.PatientInsuranceInformation.Payor.Medicare,
  api.PatientInsuranceInformation.Payor.UhcCommunityMedicaidEvercare,
  api.PatientInsuranceInformation.Payor.UnitedHealthcareMedicare,
  api.PatientInsuranceInformation.Payor.WellcareMedicareAdvantage,
]);

export function isValidSubscriber(
  subscribers: api.PrevisitPatientInsurance.PrevisitSubscriber[],
  subscriberId: string | null,
  collectSubscriberToGuarantorRelationship: boolean = false,
): boolean {
  if (!subscriberId) return false;
  const selectedSubscriber = subscribers?.find(
    (subscriber) => subscriber.id === subscriberId,
  );
  if (
    selectedSubscriber?.patientRelationshipToPrimaryPolicyholder ===
    api.Common.Relationship.Self
  ) {
    return true;
  }
  if (
    !selectedSubscriber ||
    !selectedSubscriber.primaryPolicyHolderFirstName?.trim() ||
    !selectedSubscriber.primaryPolicyHolderLastName?.trim() ||
    !selectedSubscriber.patientRelationshipToPrimaryPolicyholder ||
    !selectedSubscriber.primaryPolicyHolderSex ||
    !selectedSubscriber.primaryPolicyHolderDob ||
    isNaN(new Date(selectedSubscriber.primaryPolicyHolderDob).valueOf()) ||
    !selectedSubscriber.primaryPolicyHolderAddress ||
    !selectedSubscriber.primaryPolicyHolderHomePhone ||
    !selectedSubscriber.primaryPolicyHolderEmploymentStatus ||
    (EMPLOYMENT_STATUS_SHOW_EMPLOYER.includes(
      selectedSubscriber.primaryPolicyHolderEmploymentStatus,
    ) &&
      !selectedSubscriber.primaryPolicyHolderEmployer?.trim()) ||
    (collectSubscriberToGuarantorRelationship &&
      !selectedSubscriber.primaryPolicyHolderRelationshipToGuarantor)
  ) {
    return false;
  }
  return true;
}

export function showSubscriberToGuarantorRelationshipInput(
  guarantor?: api.Guarantor.PrevisitGuarantor,
  subscriber?: api.PrevisitPatientInsurance.PrevisitSubscriber,
): boolean {
  if (!guarantor || !subscriber) return false;

  if (
    guarantor.relationshipToPatient === api.Common.Relationship.Self ||
    subscriber.patientRelationshipToPrimaryPolicyholder ===
      api.Common.Relationship.Self
  ) {
    return false;
  }

  // simplified check for subscribers coming from guarantors; assumes no collision of IDs
  if (subscriber.id === guarantor.id) return false;

  return !_.some(
    [
      guarantor.firstName,
      guarantor.lastName,
      subscriber.primaryPolicyHolderFirstName,
      subscriber.primaryPolicyHolderLastName,
    ],
    (v) => !v,
  );
}

export type SubscriberKey =
  keyof api.PrevisitPatientInsurance.PrevisitSubscriber;
export type InsuranceKey = keyof api.PrevisitPatientInsurance.InsuranceCoverage;

export function getRequiredSubscriberFields(
  subscriber: api.PrevisitPatientInsurance.PrevisitSubscriber,
  practicePreferences: {
    enablePrevisitSubscriberToGuarantorRelationshipCollection: boolean;
  },
  guarantor?: api.Guarantor.PrevisitGuarantor,
): (SubscriberKey | SubscriberKey[])[] {
  const {
    patientRelationshipToPrimaryPolicyholder,
    primaryPolicyHolderEmploymentStatus,
  } = subscriber;
  const requiredFields: (SubscriberKey | SubscriberKey[])[] = [];
  if (
    patientRelationshipToPrimaryPolicyholder === api.Common.Relationship.Self
  ) {
    return requiredFields;
  }

  requiredFields.push(
    'primaryPolicyHolderFirstName',
    'primaryPolicyHolderLastName',
    'patientRelationshipToPrimaryPolicyholder',
    'primaryPolicyHolderSex',
    'primaryPolicyHolderDob',
    ['primaryPolicyHolderAddress', 'primaryPolicyHolderAddressId'], // need to allow both this and ID
    'primaryPolicyHolderHomePhone',
  );

  if (primaryPolicyHolderEmploymentStatus) {
    requiredFields.push('primaryPolicyHolderEmploymentStatus');
  }

  const { enablePrevisitSubscriberToGuarantorRelationshipCollection } =
    practicePreferences;
  if (
    enablePrevisitSubscriberToGuarantorRelationshipCollection &&
    showSubscriberToGuarantorRelationshipInput(guarantor, subscriber)
  ) {
    requiredFields.push('primaryPolicyHolderRelationshipToGuarantor');
  }

  return requiredFields;
}

export function getRequiredInsuranceFields(): InsuranceKey[] {
  const requiredFields: InsuranceKey[] = [
    'payor',
    'subscriberId',
    'primaryPolicyHolderMemberId',
  ];
  return requiredFields;
}

export function isValidMemberId(
  payor: api.PatientInsuranceInformation.Payor | string,
  subscriberId: string,
): boolean {
  switch (payor) {
    case api.PatientInsuranceInformation.Payor.Medicare: {
      // https://www.cms.gov/medicare/new-medicare-card/understanding-the-mbi.pdf
      const C = '(?:[1-9])'; // Numeric 1-9
      const N = '(?:[0-9])'; // Numeric 0-9
      const A = '(?:a|c|d|e|f|g|h|j|k|m|n|p|q|r|t|u|v|w|x|y)'; // Alphabetic character (excluding S, L, O, I, B, Z)
      const AN = `(?:${A}|${N})`;
      const subscriberRegex = new RegExp(
        `^${C}${A}${AN}${N}${A}${AN}${N}${A}${A}${N}${N}$`,
        'i',
      );

      // Used for outdated Medicare cards
      const oldSubscriberRegex = /\d{9}[a-z]/i;

      if (
        !subscriberRegex.test(subscriberId) &&
        !oldSubscriberRegex.test(subscriberId)
      ) {
        return false;
      }
    }
  }

  return true;
}

export function isValidInsuranceCoverage(args: {
  insuranceCoverage: api.PrevisitPatientInsurance.InsuranceCoverage;
  patient: api.Patient.Patient;
  encounter: api.Encounter.Encounter | null;
  onError: (errorMessage: string) => void;
}) {
  const { insuranceCoverage, patient, encounter, onError } = args;
  const {
    id,
    payor,
    primaryPolicyHolderMemberId,
    coverageStartDate,
    subscriberId,
  } = insuranceCoverage;
  if (!id || !payor || !primaryPolicyHolderMemberId?.trim()) {
    onError('Please fill out your insurance information.');
    return false;
  }
  if (!subscriberId) {
    onError('Please select the subscriber.');
    return false;
  }
  if (!isValidMemberId(payor, primaryPolicyHolderMemberId)) {
    onError(`Invalid ${payor} ID`);
    return false;
  }
  if (
    coverageStartDate &&
    (isNaN(new Date(coverageStartDate).valueOf()) ||
      (patient?.dateOfBirth &&
        moment(coverageStartDate).isBefore(patient.dateOfBirth)) ||
      (encounter?.startTime &&
        moment(coverageStartDate).isAfter(encounter.startTime)))
  ) {
    onError('Invalid coverage start date.');
    return false;
  }
  return true;
}

export function convertSubscriberToNamed(
  subscriber: api.PrevisitPatientInsurance.PrevisitSubscriber,
): Named {
  return {
    id: subscriber.id,
    lastName: subscriber.primaryPolicyHolderLastName,
    firstName: subscriber.primaryPolicyHolderFirstName,
    relationshipToPatient: subscriber.patientRelationshipToPrimaryPolicyholder,
    newlyAdded: subscriber.newlyAdded,
  };
}

export function getBlankSubscriber(): Omit<
  api.PrevisitPatientInsurance.PrevisitSubscriber,
  'id' | 'newlyAdded'
> {
  return {
    primaryPolicyHolderFirstName: null,
    primaryPolicyHolderMiddleName: null,
    primaryPolicyHolderLastName: null,
    primaryPolicyHolderDob: null,
    primaryPolicyHolderMemberId: null,
    primaryPolicyHolderSex: null,
    primaryPolicyHolderAddressId: null,
    primaryPolicyHolderHomePhone: null,
    primaryPolicyHolderWorkPhone: null,
    primaryPolicyHolderEmployer: null,
    primaryPolicyHolderEmploymentStatus: null,
    primaryPolicyHolderEmploymentStatusRaw: null,
    primaryPolicyHolderRetirementDate: null,
    primaryPolicyHolderRelationshipToGuarantor: null,
    primaryPolicyHolderRelationshipToGuarantorRaw: null,
    patientRelationshipToPrimaryPolicyholder: null,
    primaryPolicyHolderAddress: null,
  };
}

export const cleanInsuranceCoverage = (
  insuranceCoverage: api.PrevisitPatientInsurance.InsuranceCoverage,
): api.PrevisitPatientInsurance.InsuranceCoverage => {
  return {
    ...insuranceCoverage,
    groupNumber:
      api.PatientInsuranceInformation.PAYORS_NO_GROUP_NUMBER.includes(
        insuranceCoverage.payor as any,
      )
        ? null
        : insuranceCoverage.groupNumber,
  };
};
